import * as yup from 'yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography, useTheme } from '@mui/material';

import LoadingButton from '@mui/lab/LoadingButton';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import ControlledPasswordField from '@frontend/features/common/components/ControlledPasswordField';
import { client } from '@frontend/features/common/services';

import { AuthScreens } from '../types';

interface Props {
  onChangeScreen: (nextPage: AuthScreens) => (token?: string) => void;
  token?: string;
}

interface FormValues {
  newPassword: string;
  confirmNewPassword: string;
}

const resolver = yupResolver(
  yup.object().shape({
    newPassword: yup.string().required(),
    confirmNewPassword: yup
      .string()
      .oneOf([yup.ref('newPassword')], 'auth.resetPassword.passwordsDontMatch')
      .required('common.form.required'),
  }),
);

export const ResetPassword = ({ onChangeScreen, token }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { addToast } = useSnackBar();
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<FormValues>({
    resolver,
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);
      await client.post('auth/reset-password', data, { headers: { Authorization: `bearer ${token}` } });
      addToast({
        message: t('auth.resetPassword.success'),
        color: 'success',
      });

      onChangeScreen('login')();
    } catch (_error) {
      addToast({
        message: t('auth.resetPassword.error'),
        color: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-around" padding={2} minWidth={300}>
      <Typography variant="h6" mb={theme.spacing(4)}>
        {t('auth.resetPassword.title')}
      </Typography>
      <Box
        component="form"
        display="flex"
        flexDirection="column"
        gap={theme.spacing(2)}
        onSubmit={handleSubmit(onSubmit)}
      >
        <ControlledPasswordField
          required
          fullWidth
          control={control}
          name="newPassword"
          size="small"
          placeholder={t('auth.resetPassword.newPassword')}
          errorMessage={t('common.form.required')}
        />

        <ControlledPasswordField
          required
          fullWidth
          control={control}
          name="confirmNewPassword"
          size="small"
          placeholder={t('auth.resetPassword.confirmNewPassword')}
          errorMessage={errors.confirmNewPassword && t(`${errors.confirmNewPassword.message}`)}
        />

        <Box display="flex" justifyContent="flex-end" gap={theme.spacing(1)} pt={theme.spacing(3)}>
          <LoadingButton
            loading={isLoading}
            variant="outlined"
            color="primary"
            onClick={() => onChangeScreen('login')()}
          >
            {t('common.cancel')}
          </LoadingButton>
          <LoadingButton loading={isLoading} type="submit" variant="contained" color="primary">
            {t('auth.resetPassword.save')}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};
